import { AxiosResponse } from 'axios';
import { config } from '@/config/config';

/**
 * If uploadUrl is relative, resolve it against the API base URL so PUT goes to the API, not the current page origin.
 * Presigned S3/R2 URLs are absolute and are returned unchanged.
 */
export function getAbsoluteUploadUrl(uploadUrl: string): string {
  if (
    !uploadUrl ||
    uploadUrl.startsWith('http://') ||
    uploadUrl.startsWith('https://')
  ) {
    return uploadUrl;
  }
  const base = config.apiBaseUrl.replace(/\/$/, '');
  const path = uploadUrl.startsWith('/') ? uploadUrl : `/${uploadUrl}`;
  return `${base}${path}`;
}

/**
 * Upload-init can return { uploadUrl, key } at top level (NestJS)
 * or wrapped in body/data (Lambda/API Gateway). Normalize to a single shape.
 */
export function parseUploadInitResponse(
  res: AxiosResponse<unknown> | { data: unknown }
): { uploadUrl: string; key: string } {
  const raw = res?.data;
  if (!raw || typeof raw !== 'object') {
    throw new Error('Upload init did not return a valid response');
  }

  const obj = raw as Record<string, unknown>;
  let payload: Record<string, unknown> = obj;

  if (typeof obj.body === 'string') {
    try {
      payload = JSON.parse(obj.body) as Record<string, unknown>;
    } catch {
      // fall through and use obj
    }
  } else if (obj.body && typeof obj.body === 'object') {
    payload = obj.body as Record<string, unknown>;
  } else if (obj.data && typeof obj.data === 'object') {
    payload = obj.data as Record<string, unknown>;
  }

  const uploadUrl =
    typeof payload.uploadUrl === 'string'
      ? payload.uploadUrl
      : typeof payload.url === 'string'
        ? payload.url
        : '';
  const key = typeof payload.key === 'string' ? payload.key : '';

  if (!uploadUrl || !key) {
    throw new Error('Upload init did not return uploadUrl and key');
  }
  return { uploadUrl, key };
}
